import type {
  ReactionEmoji,
  VideoChapter,
  VideoFeedback,
  VideoLinks,
  VideoSummary,
  VideoTranscript,
} from '@live-stream/shared'
import type { SharedVideo, Videos, VideoUpdate } from '../api/videos.js'
import { ApiError } from '../http.js'
import { derived, patch, readonly, watch, writable, type ReadableStore } from '../store.js'
import { CutEditor } from './cut-editor.js'
import { countOf, marks, passed, type Mark } from './feedback.js'
import { flatten, type FlatSentence } from './transcript.js'
import { PlayedRanges } from './watch.js'

/**
 * A video's share page (Phase 9): `/v/<token>`, for whoever holds the link.
 *
 * Everything the page knows and does that is not the player itself: the
 * video and its links, what stands between a viewer and it (a password, a
 * sign-in, an expiry), looking again while it is still being prepared, the
 * transcript, the comments and reactions, Claude's suggestions, the owner's
 * edits, and counting what this viewer played. The cut editor's draft is
 * `editor`.
 *
 * `open(token)` when the page shows a link (again when the address moves to
 * another) and `stop()` when it goes; between them it polls and reports on
 * its own. The player stays the page's: it tells this where the playhead
 * is (`tick`, `playbackStarted`, `playbackStopped`).
 */

/** What stands between a viewer and the video. */
export type VideoGate = 'password' | 'sign_in' | 'expired' | 'private'

export interface VideoPageState {
  video: VideoSummary | null
  links: VideoLinks | null
  /** The viewer owns it: they edit it, and nothing they watch is counted. */
  mine: boolean
  loading: boolean
  /** The last load or action that failed, for showing. */
  error: string | null
  gate: VideoGate | null
  unlocking: boolean
  unlockError: string | null
  transcript: VideoTranscript | null
  transcriptBusy: boolean
  insightsBusy: boolean
  /** Saving the title and description. */
  saving: boolean
  /** Comments and reactions, once the video is ready. */
  feedback: VideoFeedback | null
}

/** A comment's mark on the timeline, with the comment to jump to. */
export type CommentMark = Mark & { id: string | null }

/**
 * The best file for the player: the MP4 once it exists, the source before.
 * While editing, the original: the editor's clock is the original's.
 */
export function playerSource(links: VideoLinks | null, editing: boolean): string | null {
  if (!links) return null
  return editing ? (links.source ?? null) : (links.mp4 ?? links.source ?? null)
}

const PREPARING_POLL_MS = 4000
const FEEDBACK_POLL_MS = 15_000
const WATCH_FLUSH_MS = 10_000

const sameChapters = (a: readonly VideoChapter[], b: readonly VideoChapter[]): boolean =>
  a.length === b.length && a.every((c, i) => c.atMs === b[i]!.atMs && c.title === b[i]!.title)

const messageOf = (err: unknown, fallback: string): string =>
  err instanceof Error ? err.message : fallback

const pageVisible = (): boolean =>
  typeof document === 'undefined' || document.visibilityState === 'visible'

const initialState = (): VideoPageState => ({
  video: null,
  links: null,
  mine: false,
  loading: true,
  error: null,
  gate: null,
  unlocking: false,
  unlockError: null,
  transcript: null,
  transcriptBusy: false,
  insightsBusy: false,
  saving: false,
  feedback: null,
})

export class VideoPage {
  private readonly store = writable<VideoPageState>(initialState())
  readonly state: ReadableStore<VideoPageState> = readonly(this.store)
  readonly video: ReadableStore<VideoSummary | null>
  /** Still being prepared, the MP4 on its way, the cuts rendering: it looks again on its own. */
  readonly preparing: ReadableStore<boolean>
  /** The transcript's sentences, in order, each with its paragraph. */
  readonly sentences: ReadableStore<FlatSentence[]>
  /** Claude's title, when it differs from the one the video has. */
  readonly suggestedTitle: ReadableStore<string | null>
  /** Claude's chapters, when they differ from the video's. */
  readonly suggestedChapters: ReadableStore<VideoChapter[] | null>
  /** Claude is writing the summary. */
  readonly writing: ReadableStore<boolean>
  readonly commentMarks: ReadableStore<CommentMark[]>
  readonly reactionMarks: ReadableStore<Mark[]>
  /** The owner may open the cut editor now. */
  readonly canEdit: ReadableStore<boolean>
  /** Cuts are applied to it now. */
  readonly hadCuts: ReadableStore<boolean>
  readonly editor: CutEditor

  private shared: SharedVideo | null = null
  private ranges = new PlayedRanges()
  private watchId: string | null = null
  private watchStarting = false
  private lastMs = 0
  private preparingPoll: ReturnType<typeof setInterval> | null = null
  private feedbackPoll: ReturnType<typeof setInterval> | null = null
  private watchFlush: ReturnType<typeof setInterval> | null = null
  private unwatchPreparing: (() => void) | null = null

  constructor(
    private readonly videos: Videos,
    private readonly signedIn: ReadableStore<boolean>,
  ) {
    const s = this.store
    this.video = derived([s], (x) => x.video)
    this.preparing = derived([this.video], (v) =>
      v === null
        ? false
        : v.status === 'processing' ||
          v.status === 'uploading' ||
          (v.status === 'ready' &&
            (!v.hasMp4 ||
              !v.optimized ||
              v.rendering ||
              v.transcript === 'pending' ||
              v.transcript === 'processing' ||
              v.insights.status === 'pending' ||
              v.insights.status === 'processing')),
    )
    const transcript = derived([s], (x) => x.transcript)
    this.sentences = derived([transcript], (t) => (t ? flatten(t.paragraphs) : []))
    this.suggestedTitle = derived([this.video], (v) => {
      const t = v?.insights.title
      return t && t !== v.title ? t : null
    })
    this.suggestedChapters = derived([this.video], (v) => {
      const i = v?.insights
      if (!v || !i || i.chapters.length === 0) return null
      return sameChapters(i.chapters, v.chapters) ? null : i.chapters
    })
    this.writing = derived(
      [this.video],
      (v) => v?.insights.status === 'pending' || v?.insights.status === 'processing',
    )
    const feedback = derived([s], (x) => x.feedback)
    this.commentMarks = derived([feedback, this.video], (f, v) => {
      const timed = (f?.comments ?? []).filter((c) => c.parentId === null && c.atMs !== null)
      return marks(
        timed.map((c) => c.atMs!),
        v?.durationMs ?? 0,
      ).map((m) => ({ ...m, id: timed.find((c) => c.atMs === m.atMs)?.id ?? null }))
    })
    this.reactionMarks = derived([feedback, this.video], (f, v) =>
      marks(
        (f?.reactions.moments ?? []).map((m) => m.atMs),
        v?.durationMs ?? 0,
        0.006,
      ),
    )
    const mine = derived([s], (x) => x.mine)
    this.canEdit = derived(
      [mine, this.video],
      (m, v) =>
        m &&
        v !== null &&
        v.status === 'ready' &&
        v.hasMp4 &&
        !v.rendering &&
        (v.sourceDurationMs ?? 0) > 0,
    )
    this.hadCuts = derived([this.video], (v) => (v?.keep.length ?? 0) > 0)
    this.editor = new CutEditor(videos, {
      video: this.video,
      adopt: (video) => patch(this.store, { video }),
      fail: (err, fallback) => this.fail(err, fallback),
    })
  }

  /** The share token this page follows; null before `open()`. */
  get token(): string | null {
    return this.shared?.token ?? null
  }

  /** The link this page follows, for panels that act on it (comments, say). */
  get link(): SharedVideo {
    if (!this.shared) throw new Error('VideoPage: open(token) first.')
    return this.shared
  }

  /**
   * Show the video behind `token`, and keep it up to date until `stop()`.
   * The link it already shows (after a link reset of its own) changes
   * nothing; another starts over.
   */
  open(token: string): void {
    if (this.shared?.token === token) return
    this.stop()
    this.shared = this.videos.shared(token)
    this.ranges = new PlayedRanges()
    this.watchId = null
    this.lastMs = 0
    this.store.set(initialState())
    this.editor.reset()
    void this.load()
    this.unwatchPreparing = watch(
      this.preparing,
      (on) => on,
      (on) => {
        if (on && !this.preparingPoll) {
          this.preparingPoll = setInterval(() => void this.load(true), PREPARING_POLL_MS)
        } else if (!on && this.preparingPoll) {
          clearInterval(this.preparingPoll)
          this.preparingPoll = null
        }
      },
      { immediate: true },
    )
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibility)
      window.addEventListener('pagehide', this.onPageHide)
    }
  }

  /** The page is going: stop polling, and send what was watched as a request that outlives it. Idempotent. */
  stop(): void {
    this.unwatchPreparing?.()
    this.unwatchPreparing = null
    for (const timer of [this.preparingPoll, this.feedbackPoll, this.watchFlush]) {
      if (timer) clearInterval(timer)
    }
    this.preparingPoll = this.feedbackPoll = this.watchFlush = null
    // The last report takes the link before it goes.
    void this.flushWatch(true)
    this.shared = null
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibility)
      window.removeEventListener('pagehide', this.onPageHide)
    }
  }

  private readonly onVisibility = (): void => {
    if (!pageVisible()) void this.flushWatch(true)
  }

  private readonly onPageHide = (): void => {
    void this.flushWatch(true)
  }

  private fail(err: unknown, fallback: string): void {
    patch(this.store, { error: messageOf(err, fallback) })
  }

  /** `polling` says this is the page looking again, not another viewer. */
  async load(polling = false): Promise<void> {
    try {
      const r = await this.link.load(polling)
      const before = this.store.get()
      const wasRendering = before.video?.rendering ?? false
      // Do not swap the source under a playing video; links refresh on error.
      const links =
        !before.links?.source || !r.links.source
          ? r.links
          : { ...r.links, source: before.links.source, mp4: before.links.mp4 }
      patch(this.store, { gate: null, video: r.video, links, mine: r.mine, error: null })
      if (r.video.status === 'ready' && !before.feedback) {
        void this.loadFeedback()
        this.startFeedbackPoll()
      }
      // The words are fetched once they exist (or failed), and again when they change.
      if (
        (r.video.transcript === 'ready' || r.video.transcript === 'failed') &&
        this.store.get().transcript?.status !== r.video.transcript
      ) {
        await this.loadTranscript()
      }
      // The cuts have been rendered: the new MP4 and the words on its clock.
      if (wasRendering && !r.video.rendering) {
        patch(this.store, { links: r.links })
        await this.loadTranscript()
      }
    } catch (err) {
      this.onLoadError(err)
    } finally {
      patch(this.store, { loading: false })
    }
  }

  private onLoadError(err: unknown): void {
    const api = err instanceof ApiError ? err : null
    if (api?.code === 'password_required') {
      // A stored key that no longer opens it: the password changed.
      this.link.forgetUnlock()
      patch(this.store, { gate: 'password', video: null })
    } else if (api?.code === 'sign_in_required') {
      patch(this.store, { gate: 'sign_in', video: null })
    } else if (api?.code === 'expired') {
      patch(this.store, { gate: 'expired', video: null })
    } else if (api?.status === 404) {
      patch(this.store, { error: 'This video does not exist, or its link has changed.' })
    } else if (api?.status === 403) {
      patch(this.store, {
        error: this.signedIn.get()
          ? 'This video is private.'
          : 'This video is private. Sign in if it is yours.',
      })
    } else {
      this.fail(err, 'Could not load the video.')
    }
  }

  /** Try the password; the video loads when it opens it. The server says when it is wrong. */
  async unlock(password: string): Promise<void> {
    if (!password) return
    patch(this.store, { unlocking: true, unlockError: null })
    try {
      await this.link.unlock(password)
      patch(this.store, { gate: null, loading: true })
      await this.load()
    } catch (err) {
      patch(this.store, { unlockError: messageOf(err, 'Could not check the password.') })
    } finally {
      patch(this.store, { unlocking: false })
    }
  }

  /** A signed link ran out mid-play: fresh links, the player picks up where it was. */
  async refreshLinks(): Promise<void> {
    if (this.store.get().video?.status !== 'ready') return
    patch(this.store, { links: null })
    await this.load(true)
  }

  // ---- The transcript.

  private async loadTranscript(): Promise<void> {
    try {
      patch(this.store, { transcript: await this.link.transcript() })
    } catch {
      // The summary's word on where it stands is enough.
    }
  }

  /** The WebVTT captions for the player, once there is a transcript. */
  captionsUrl(): string | null {
    return this.store.get().video?.transcript === 'ready' ? this.link.captionsUrl() : null
  }

  /** The owner asks for the transcript, or for it again after a failure. */
  async requestTranscript(): Promise<void> {
    const video = this.store.get().video
    if (!video) return
    patch(this.store, { transcriptBusy: true })
    try {
      patch(this.store, { video: await this.videos.transcribe(video.id), transcript: null })
    } catch (err) {
      this.fail(err, 'Could not start the transcript.')
    } finally {
      patch(this.store, { transcriptBusy: false })
    }
  }

  // ---- What Claude wrote: the summary for everyone, suggestions for the owner.

  async requestInsights(): Promise<void> {
    await this.insights('Could not ask for a summary.', (id) => this.videos.suggest(id))
  }

  async takeSuggestion(what: { title?: boolean; chapters?: boolean }): Promise<void> {
    await this.insights('Could not take the suggestion.', (id) =>
      this.videos.applySuggestion(id, what),
    )
  }

  private async insights(
    fallback: string,
    run: (videoId: string) => Promise<VideoSummary>,
  ): Promise<void> {
    const video = this.store.get().video
    if (!video) return
    patch(this.store, { insightsBusy: true })
    try {
      patch(this.store, { video: await run(video.id) })
    } catch (err) {
      this.fail(err, fallback)
    } finally {
      patch(this.store, { insightsBusy: false })
    }
  }

  // ---- The owner's edits.

  /** Title and description; true once saved. */
  async save(change: Pick<VideoUpdate, 'title' | 'description'>): Promise<boolean> {
    const video = this.store.get().video
    if (!video) return false
    patch(this.store, { saving: true })
    try {
      const next = await this.videos.update(video.id, {
        title: change.title?.trim(),
        description: change.description?.trim(),
      })
      patch(this.store, { video: next })
      return true
    } catch (err) {
      this.fail(err, 'Could not save.')
      return false
    } finally {
      patch(this.store, { saving: false })
    }
  }

  /** Delete it for everyone. */
  async remove(): Promise<void> {
    const video = this.store.get().video
    if (video) await this.videos.remove(video.id)
  }

  /** A newer copy of the video, from a panel that changed it. */
  adopt(video: VideoSummary): void {
    patch(this.store, { video })
  }

  /** A new thumbnail: the player keeps its file, only the picture changes. */
  adoptThumbnail(r: { video: VideoSummary; links: VideoLinks }): void {
    const links = this.store.get().links
    patch(this.store, {
      video: r.video,
      ...(links ? { links: { ...links, poster: r.links.poster } } : {}),
    })
  }

  /**
   * New sharing settings. A new link moves the page to its token, keeping
   * everything loaded; true when it moved, for the page to change address.
   */
  adoptSharing(video: VideoSummary): boolean {
    const moved = video.shareToken !== this.shared?.token
    if (moved) this.shared = this.videos.shared(video.shareToken)
    patch(this.store, { video })
    return moved
  }

  // ---- Comments and reactions.

  async loadFeedback(): Promise<void> {
    try {
      patch(this.store, { feedback: await this.link.feedback() })
    } catch {
      // The video plays without them.
    }
  }

  /** Others' comments and reactions arrive while the page is open and looked at. */
  private startFeedbackPoll(): void {
    if (this.feedbackPoll) return
    this.feedbackPoll = setInterval(() => {
      if (pageVisible() && this.store.get().video?.status === 'ready') void this.loadFeedback()
    }, FEEDBACK_POLL_MS)
  }

  /** React at `atMs`, counted at once; a refused one (too many at once) is let go. */
  async react(emoji: ReactionEmoji, atMs: number): Promise<void> {
    const at = Math.round(atMs)
    const feedback = this.store.get().feedback
    if (feedback) {
      const r = feedback.reactions
      patch(this.store, {
        feedback: {
          ...feedback,
          reactions: {
            ...r,
            counts: { ...r.counts, [emoji]: countOf(r.counts, emoji) + 1 },
            moments: [...r.moments, { emoji, atMs: at }],
          },
        },
      })
    }
    try {
      await this.link.react(emoji, at)
    } catch {
      // The reaction showed; that was the point.
    }
  }

  // ---- The player's clock, and what this viewer watched.

  /**
   * The playhead moved to `ms`. Counts what played (not the owner's, not
   * while editing), and returns the reactions others sent at the moments
   * just played, to float up again.
   */
  tick(ms: number, playing: boolean, editing = false): ReactionEmoji[] {
    const s = this.store.get()
    const from = this.lastMs
    this.lastMs = ms
    if (!s.mine && !editing) this.ranges.observe(ms, playing)
    if (editing || !playing || !s.feedback) return []
    return passed(s.feedback.reactions.moments, from, ms).map((m) => m.emoji)
  }

  /** Playback started: begin counting this view (once). */
  async playbackStarted(): Promise<void> {
    const s = this.store.get()
    if (s.mine || this.watchId || this.watchStarting || s.video?.status !== 'ready') return
    this.watchStarting = true
    try {
      this.watchId = await this.link.startWatch()
      if (this.watchId && !this.watchFlush) {
        this.watchFlush = setInterval(() => void this.flushWatch(), WATCH_FLUSH_MS)
      }
    } catch {
      // Not counted, then; the video plays the same.
    } finally {
      this.watchStarting = false
    }
  }

  /** Paused or ended at `ms`: close the stretch and send it. */
  playbackStopped(ms: number): void {
    this.ranges.observe(ms, false)
    void this.flushWatch()
  }

  /** Send what played since the last send; `leaving` as a request that outlives the page. */
  private async flushWatch(leaving = false): Promise<void> {
    if (!this.watchId || !this.ranges.pending) return
    const ranges = this.ranges.take()
    try {
      await this.link.reportWatch(this.watchId, ranges, leaving)
    } catch {
      // A lost batch is a few seconds missing from the numbers.
    }
  }
}
